import { Link } from "react-router-dom";
import { ArrowRight, Target, Users, School, HeartHandshake, ShieldCheck, BadgeCheck, Shield, MapPin, CalendarDays, Star } from "lucide-react";
import Marquee from "@/components/Marquee";
import BookingDialog from "@/components/BookingDialog";
import { SERVICES, COACHES } from "@/constants/site";

const ICONS = { Target, Users, School, HeartHandshake };

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1517747614396-d21a78b850e8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" alt="Stadium" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/70 via-[#020617]/85 to-[#020617]" />
          <div className="absolute inset-0 pitch-stripes" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-28 sm:py-40 grid lg:grid-cols-12 gap-10 items-center text-white">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6">
              <span className="w-2 h-2 rounded-full bg-[#FBBF24] animate-pulse" />
              <span className="eyebrow text-white">Now booking · 1-to-1 sessions</span>
            </div>
            <h1 className="font-display heading-uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] font-bold text-white">
              Elite coaching.<br />
              <span className="text-[#FBBF24]">Developing all abilities.</span>
            </h1>
            <p className="mt-6 max-w-xl text-slate-200 text-base sm:text-lg leading-relaxed">
              1-to-1 sessions and small groups led by FA qualified, DBS-checked coaches.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <BookingDialog
                trigger={
                  <button data-testid="hero-book-btn" className="btn-gold">
                    Book a Session <ArrowRight className="w-4 h-4" />
                  </button>
                }
              />
              <Link to="/private-sessions" data-testid="hero-sessions-btn" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-display font-bold uppercase tracking-[0.16em] hover:bg-white hover:text-[#1E3A8A] transition">
                See Sessions
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              {[
                { icon: ShieldCheck, label: "FA Qualified" },
                { icon: BadgeCheck, label: "DBS Checked" },
                { icon: Shield, label: "Safeguarding Trained" },
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full backdrop-blur-md">
                    <Icon className="w-4 h-4 text-[#FBBF24]" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-white">{b.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#F59E0B]/20 rounded-3xl blur-2xl" />
              <div className="relative bg-white text-slate-900 border border-slate-200 rounded-3xl p-6 shadow-2xl">
                <div className="eyebrow text-[#1E3A8A] mb-2">Now Booking</div>
                <div className="font-display text-2xl uppercase leading-tight">1-to-1 Coaching</div>
                <div className="mt-1 text-slate-500 text-sm flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Mon–Thu evenings</div>
                <div className="mt-1 text-slate-500 text-sm flex items-center gap-2"><MapPin className="w-4 h-4" /> Billingham, Teesside</div>
                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">From</div>
                    <div className="font-display text-3xl text-[#1E3A8A]">£35<span className="text-sm font-normal text-slate-500">/session</span></div>
                  </div>
                  <BookingDialog
                    defaultService="one_to_one"
                    trigger={<button data-testid="hero-next-session-btn" className="btn-gold text-xs px-5 py-3">Reserve <ArrowRight className="w-3 h-3" /></button>}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES — BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
          <div>
            <div className="eyebrow text-[#1E3A8A]">What we do</div>
            <h2 className="font-display heading-uppercase text-4xl sm:text-5xl leading-none mt-3">Pathways for <span className="text-[#1E3A8A]">every player.</span></h2>
          </div>
          <p className="max-w-md text-slate-500">Whether your child is taking their first touch or chasing trials, we have a programme to push them forward.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-5">
          {SERVICES.map((s, idx) => {
            const Icon = ICONS[s.icon];
            const span = idx === 0 ? "md:col-span-4 md:row-span-2" : idx === 1 || idx === 2 ? "md:col-span-2" : "md:col-span-2";
            return (
              <Link
                key={s.slug}
                to={s.href}
                data-testid={`service-card-${s.slug}`}
                className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 hover-lift ${span} ${idx === 0 ? "min-h-[420px]" : "min-h-[200px]"}`}
              >
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-60 group-hover:scale-105 transition" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A] via-[#1E3A8A]/85 to-[#1E3A8A]/40" />
                <div className="relative p-6 sm:p-8 h-full flex flex-col justify-end text-white">
                  <div className="w-12 h-12 rounded-xl bg-[#FBBF24] text-[#1E3A8A] grid place-items-center mb-4">
                    {Icon && <Icon className="w-6 h-6" />}
                  </div>
                  <h3 className="font-display uppercase text-2xl sm:text-3xl leading-tight text-white">{s.title}</h3>
                  <p className="mt-2 text-slate-100 text-sm max-w-md">{s.blurb}</p>
                  <div className="mt-4 inline-flex items-center gap-2 text-[#FBBF24] text-xs font-semibold uppercase tracking-widest">
                    {s.cta} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* COACHES */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
          <div className="mb-12">
            <div className="eyebrow text-[#1E3A8A]">Our coaches</div>
            <h2 className="font-display heading-uppercase text-4xl sm:text-5xl leading-none mt-3">Our Coaches</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {COACHES.map((c, i) => (
              <Link
                to={`/private-sessions?coach=${c.slug}#availability`}
                key={c.slug}
                data-testid={`coach-card-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white hover-lift focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/40"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <h3 className="font-display uppercase text-2xl group-hover:text-[#1E3A8A] transition-colors">{c.name}</h3>
                  <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[#1E3A8A]">
                    See availability <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32">
        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-3xl border border-[#F59E0B]/30 bg-gradient-to-br from-[#1E3A8A] to-[#0B1B3A] p-10 sm:p-14 grain text-white text-center">
            <div className="eyebrow text-[#FBBF24]">Ready to play?</div>
            <h3 className="font-display heading-uppercase text-3xl sm:text-4xl mt-3 leading-tight text-white">Reserve your spot <br />in two minutes.</h3>
            <p className="mt-3 text-slate-200 max-w-md mx-auto">Pay online or pay at the session — your choice. Limited spaces per group.</p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <BookingDialog trigger={<button data-testid="cta-book-btn" className="btn-gold">Book Now <ArrowRight className="w-4 h-4" /></button>} />
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white text-white font-display font-bold uppercase tracking-[0.16em] hover:bg-white hover:text-[#1E3A8A] transition" data-testid="cta-contact-btn">Ask a Question</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
